import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { Observable, firstValueFrom, map, take, timeout } from 'rxjs';
import { syncContext } from 'src/app/core/interceptors/auth-context';
import { environment } from 'src/environments/environment';

export const PUSH_NOT_CONFIGURED = 'Push ist nicht eingerichtet';
const USER_AGENT_MAX = 512;
const LOGOUT_DELETE_TIMEOUT_MS = 3000;

@Injectable({ providedIn: 'root' })
export class PushSubscriptionService {
  private readonly http = inject(HttpClient);
  private readonly swPush = inject(SwPush);
  private readonly envUrl = environment.apiUrl;
  private publicKey: string | null = null;
  private currentSubscription: PushSubscription | null = null;

  constructor() {
    // Abo mitführen, damit der Logout die Anfrage ohne Wartezeit absetzen kann
    if (this.swPush.isEnabled) {
      this.swPush.subscription.subscribe((subscription) => (this.currentSubscription = subscription));
    }
  }

  /** true, wenn der Service Worker aktiv ist und der Browser Push kennt */
  isSupported(): boolean {
    return this.swPush.isEnabled && typeof window !== 'undefined' && 'PushManager' in window;
  }

  /** iPhone/iPad im Browser-Tab: Push geht dort nur für die zum Home-Bildschirm hinzugefügte App */
  isIosWithoutInstall(): boolean {
    const ua = navigator.userAgent;
    const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    const ios = /iPad|iPhone|iPod/.test(ua) || iPadOs;
    if (!ios) {
      return false;
    }
    const standalone =
      (navigator as Navigator & { standalone?: boolean }).standalone === true ||
      (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
    return !standalone;
  }

  get deviceSubscribed$(): Observable<boolean> {
    return this.swPush.subscription.pipe(map((subscription) => !!subscription));
  }

  /** Public Key vorab laden (Einstellungsseite), damit der Klick ohne Netzwerkaufruf abonnieren kann. null = Push am Server aus. */
  async loadPublicKey(): Promise<string | null> {
    const res = await firstValueFrom(
      this.http.get<{ data: { publicKey?: string | null } | null }>(`${this.envUrl}/push/public-key`),
    );
    this.publicKey = res.data?.publicKey ?? null;

    return this.publicKey;
  }

  /**
   * Safari/iOS verlangt eine Benutzergeste: requestSubscription wird darum vor jedem await aufgerufen,
   * der Public Key muss vorher mit loadPublicKey() geholt worden sein.
   */
  async enableOnThisDevice(): Promise<void> {
    if (!this.publicKey) {
      throw new Error(PUSH_NOT_CONFIGURED);
    }
    const subscription = await this.swPush.requestSubscription({ serverPublicKey: this.publicKey });
    try {
      const json = subscription.toJSON();
      const p256dh = json.keys?.['p256dh'];
      const auth = json.keys?.['auth'];
      if (!json.endpoint || !p256dh || !auth) {
        throw new Error('Push-Abo unvollständig');
      }
      // Joi lehnt unbekannte Felder ab (expirationTime): nur diese Felder senden
      const body = { endpoint: json.endpoint, keys: { p256dh, auth }, userAgent: navigator.userAgent.slice(0, USER_AGENT_MAX) };
      await firstValueFrom(this.http.post(`${this.envUrl}/push/subscriptions`, body));
    } catch (error) {
      // Server kennt das Gerät nicht: lokal nicht aktiv lassen
      await this.swPush.unsubscribe().catch(() => undefined);
      throw error;
    }
  }

  async disableOnThisDevice(): Promise<void> {
    const subscription = await firstValueFrom(this.swPush.subscription.pipe(take(1)));
    try {
      if (subscription) {
        await firstValueFrom(this.http.delete(`${this.envUrl}/push/subscriptions`, { body: { endpoint: subscription.endpoint } }));
      }
    } finally {
      await this.swPush.unsubscribe();
    }
  }

  /**
   * Beim Abmelden: dieses Gerät beim Server abmelden und lokal kündigen, damit auf einem geteilten Browser
   * keine Meldungen des vorigen Benutzers mehr ankommen. Gibt null zurück, wenn es nichts zu tun gibt (kein SW,
   * kein Abo); sonst ein Promise, das nie ablehnt und nach höchstens 3 s (DELETE) endet. Die DELETE-Anfrage wird
   * synchron abgesendet, der Aufrufer wartet nicht darauf. Lokal wird in jedem Fall gekündigt. `token` wird vor dem Aufräumen übergeben.
   */
  unregisterForLogout(token: string | null): Promise<void> | null {
    const subscription = this.currentSubscription;
    if (!this.swPush.isEnabled || !subscription) {
      return null;
    }

    return this.runLogoutCleanup(subscription, token);
  }

  private async runLogoutCleanup(subscription: PushSubscription, token: string | null): Promise<void> {
    try {
      if (token) {
        await firstValueFrom(
          this.http
            .delete(`${this.envUrl}/push/subscriptions`, { body: { endpoint: subscription.endpoint }, context: syncContext(token) })
            .pipe(timeout(LOGOUT_DELETE_TIMEOUT_MS)),
        );
      }
    } catch {
      // offline, Token abgelaufen oder zu langsam: der Server räumt das Abo bei der nächsten Zustellung (410) selbst auf
    }
    try {
      await this.swPush.unsubscribe();
    } catch (error) {
      console.warn('[PushSubscriptionService] logout unsubscribe failed', error instanceof Error ? error.name : 'unknown');
    }
  }
}
